import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWorkflowRunService } from '../services/workflowRunService';
import type { WorkflowAccessReport } from '../../../types/backend';

export const workflowAccessKeys = {
  check: (customerId?: string, investigationId?: string, workflowName?: string) =>
    ['workflow-access', customerId, investigationId, workflowName] as const,
};

interface Options {
  customerId: string | undefined;
  investigationId: string | undefined;
}

/**
 * The launcher's preflight: what the picked workflow needs against the operator's access now.
 * A read through the query layer (FE-13); an escalation changes the answer, so `recheck`
 * invalidates rather than reusing the last report.
 */
export const useWorkflowAccess = ({ customerId, investigationId }: Options) => {
  const service = useWorkflowRunService();
  const queryClient = useQueryClient();
  const [workflowName, setWorkflowName] = useState<string | null>(null);

  const key = workflowAccessKeys.check(customerId, investigationId, workflowName ?? undefined);
  const query = useQuery<WorkflowAccessReport>({
    queryKey: key,
    queryFn: () =>
      service.checkAccess(customerId as string, investigationId as string, workflowName as string),
    enabled: Boolean(customerId && investigationId && workflowName),
  });

  // Null while the check has errored, not the answer it gave before it did: the query layer keeps
  // the last successful data through a failed refetch, and a recheck runs exactly when the
  // operator changed their access. Holding the stale report would offer Start against an answer
  // that no longer describes them (PRIN-06).
  const report = workflowName && !query.isError ? (query.data ?? null) : null;
  const checking = Boolean(workflowName) && query.isFetching;
  // The backend's sentence, verbatim: it names the step and the access it lacks.
  const error =
    workflowName && query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Could not check your access for that workflow'
      : null;
  // Memoised: the memoised object below depends on its identity.
  const check = useCallback((name: string | null) => setWorkflowName(name), []);
  // Memoised: the launcher's escalation callback depends on its identity.
  const recheck = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['workflow-access', customerId, investigationId] }),
    [queryClient, customerId, investigationId],
  );

  // Memoised: the launcher is memo()ed and takes this object as one prop.
  return useMemo(
    () => ({ report, checking, error, check, recheck }),
    [report, checking, error, check, recheck],
  );
};
